import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { UserSubscription, User, WorkspaceItem } from './user.types';
import {
    currencyAbbrToCurrencyName,
    subscriptionKeys,
    userDtoToUser,
} from './user.converters';
import {
    GetUserRes1Dto,
    GetUserRes2Dto,
    GetUserResDto,
    ProjectService,
    RegisterUsersReqDto,
    RegisterUsersRes1Dto,
    RegisterUsersRes2Dto,
    RegisterUsersResDto,
    type SetAvatarUsers1Req1Dto,
    type SetAvatarUsers1ReqDto,
    TeamsService,
    UpdateMembersTeamsResDto,
    UpdateUserReqDto,
    UpdateUserRes1Dto,
    UpdateUserResDto,
    UsersService,
} from '../../generated';
import { CustomMutationOptions, CustomQueryOptions } from '../common.types';
import { Attachment } from '../customer/customer.types';
import {
    convertItemsToActionItemsDto,
    convertAttachmentToAttachmentDto,
} from '../common.converters';

import { isPersonalWorkspace } from './user.helpers';
import { useTeam, useTeamCache } from '../team/team.hooks';

export const useUserKey = 'useUserKey';

export const useRegister = (
    options: CustomMutationOptions<
        RegisterUsersReqDto,
        RegisterUsersResDto | RegisterUsersRes1Dto | RegisterUsersRes2Dto
    > = {}
) => {
    return useMutation({
        mutationFn: async data => {
            const response = await UsersService.registerUsers({
                body: data,
            });

            if (!response.data) throw new Error('Failed to register user');

            return response.data;
        },
        ...options,
    });
};

export const useProject = (
    key?: string,
    options: CustomQueryOptions<UserSubscription> = {}
) => {
    return useQuery({
        queryKey: [],
        queryFn: async () => {
            const response = await ProjectService.getProject({
                path: { subscription_key: subscriptionKeys[key!] },
            });

            if (!response.data) throw new Error('Failed');

            return response.data;
        },
        enabled: !!key,
        cacheTime: 0,
        ...options,
    });
};

export const useUser = (
    options: CustomQueryOptions<
        GetUserResDto | GetUserRes1Dto | GetUserRes2Dto | null
    > = {}
) => {
    const { getAccessTokenSilently } = useAuth0();
    const { data: userDto, ...rest } = useQuery({
        queryFn: async () => {
            await getAccessTokenSilently();
            const response = await UsersService.getUser({});

            if (!response.data) {
                const err = new Error('Failed to fetch user') as ApiError &
                    Error;
                err.message = response?.data?.message || 'Failed to fetch user';
                err.status = (response as any)?.status ?? 500;
                err.response = {
                    data: (response as any)?.data ?? null,
                    status: err.status,
                };
                throw err;
            }

            return response.data;
        },
        queryKey: [useUserKey],
        retry: false,
        cacheTime: 10,
        ...options,
    });
    const personalWorkspaceSelected = userDto?.account.workspaces.current
        ? isPersonalWorkspace(userDto.account.workspaces.current)
        : true;

    const { data: team, isFetching } = useTeam(
        userDto?.account.workspaces.current.membership.group.id,
        {
            enabled: !personalWorkspaceSelected,
        }
    );

    const updateDate = userDto?.last_updated?.at || userDto?.created?.at;
    const data: User | undefined = useMemo(() => {
        if (!userDto) return undefined;
        if (!personalWorkspaceSelected && !team) return undefined;

        const user = userDtoToUser(userDto as GetUserResDto);

        if (!personalWorkspaceSelected) {
            user.currency = team?.currency;
            user.currencies = team?.currencies;
            user.paymentGateways = team?.paymentGateways;
        }

        return user;
    }, [updateDate, team, personalWorkspaceSelected]);

    return {
        data,
        ...rest,
        isLoading: rest.isLoading || isFetching,
    };
};

export const useSwitchUser = (
    options: CustomMutationOptions<
        { id: string },
        UpdateUserResDto | UpdateUserRes1Dto
    > = {}
) => {
    return useMutation({
        mutationFn: async data => {
            const response = await UsersService.setCurrentWorkspaceUsers({
                path: { workspace_id: data.id },
            });

            if (!response.data) throw new Error('Failed to switch workspace');

            return response.data;
        },
        ...options,
    });
};

export const useUpdateUser = (
    options: CustomMutationOptions<
        UpdateUserReqDto | RegisterUsersReqDto,
        UpdateUserResDto | UpdateUserRes1Dto
    > = {}
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async data => {
            const response = await UsersService.updateUser({
                body: data as UpdateUserReqDto,
            });

            if (!response.data) throw new Error('Failed to update user');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            queryClient.setQueryData([useUserKey], args[0]);
            options.onSuccess?.(...args);
        },
        onError: (error, ...args) => {
            // @ts-ignore
            if (error?.status === 409) {
                options?.onSuccess?.(
                    {} as UpdateUserResDto | UpdateUserRes1Dto,
                    ...args
                );
            } else {
                options?.onError?.(error, ...args);
            }
        },
    });
};

export const useUpdateUserAttachments = (
    oldItems: Attachment[],
    options: CustomMutationOptions<
        {
            items: Attachment[];
        },
        UpdateUserResDto | UpdateUserRes1Dto | null
    > = {}
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ items }) => {
            const requestItems = convertItemsToActionItemsDto(
                oldItems.map(convertAttachmentToAttachmentDto),
                items.map(convertAttachmentToAttachmentDto),
                'id',
                (oldItem, newItem) => oldItem.id !== newItem.id
            );

            if (requestItems.length) {
                const response = await UsersService.updateAttachmentsUsers({
                    body: {
                        items: requestItems,
                    },
                });

                return response.data || null;
            }
            return null;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0]) {
                queryClient.setQueryData([useUserKey], args[0]);
                options?.onSuccess?.(...args);
            }
        },
    });
};

export const useUploadAvatarUser = (
    options: CustomMutationOptions<
        SetAvatarUsers1ReqDto | SetAvatarUsers1Req1Dto,
        UpdateUserResDto | UpdateUserRes1Dto
    > = {}
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async data => {
            const response = await UsersService.setAvatarUsers1({
                body: data,
            });
            if (!response.data) throw new Error('Failed to upload avatar');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            queryClient.setQueryData([useUserKey], args[0]);
            options?.onSuccess?.(...args);
        },
    });
};

export const useUpdateCurrency = (
    workspace?: WorkspaceItem,
    options?: CustomMutationOptions<
        {
            currency: string;
            rate: number;
        },
        UpdateUserResDto | UpdateUserRes1Dto | UpdateMembersTeamsResDto
    >
) => {
    const queryClient = useQueryClient();
    const { update } = useTeamCache();

    return useMutation({
        mutationFn: async ({ currency, rate }) => {
            if (!workspace || workspace.individual) {
                const response = await UsersService.updateCurrencyUsers({
                    body: {
                        name: currencyAbbrToCurrencyName(currency),
                        abbr: currency,
                        rate,
                    },
                });

                if (!response.data)
                    throw new Error(
                        `Failed to update currency ${currency} to ${rate}`
                    );

                return response.data;
            }
            const response = await TeamsService.updateCurrencyTeams({
                path: { team_id: workspace.id },
                body: {
                    name: currencyAbbrToCurrencyName(currency),
                    abbr: currency,
                    rate,
                },
            });

            if (!response.data)
                throw new Error(
                    `Failed to update currency ${currency} to ${rate}`
                );

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0].resource_type === 'user') {
                queryClient.setQueryData([useUserKey], args[0]);
            } else if (args[0].resource_type === 'team') {
                update(args[0].id, args[0] as UpdateMembersTeamsResDto);
            }
            options?.onSuccess?.(...args);
        },
    });
};

export const useRemoveCurrency = (
    workspace?: WorkspaceItem,
    options?: CustomMutationOptions<
        {
            currency: string;
        },
        UpdateUserResDto | UpdateUserRes1Dto | UpdateMembersTeamsResDto
    >
) => {
    const queryClient = useQueryClient();
    const { update } = useTeamCache();

    return useMutation({
        mutationFn: async ({ currency }) => {
            if (!workspace || workspace.individual) {
                const response = await UsersService.updateCurrencyUsers({
                    body: {
                        name: currencyAbbrToCurrencyName(currency),
                        abbr: currency,
                        rate: 0,
                    },
                });

                if (!response.data)
                    throw new Error('Failed to update currency');

                return response.data;
            }
            const response = await TeamsService.updateCurrencyTeams({
                path: { team_id: workspace.id },
                body: {
                    name: currencyAbbrToCurrencyName(currency),
                    abbr: currency,
                    rate: 0,
                },
            });

            if (!response.data) throw new Error('Failed to update currency');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            if (args[0].resource_type === 'user') {
                queryClient.setQueryData([useUserKey], args[0]);
            } else if (args[0].resource_type === 'team') {
                update(args[0].id, args[0] as UpdateMembersTeamsResDto);
            }
            options?.onSuccess?.(...args);
        },
    });
};
